import Hue from '@assets/hue_yellow.svg';
import Image from '@atoms/Image/Image';
import { IFeature } from '@models/IFeature';
import HorizontalDirectionEnum from '@models/enums/HorizontalDirectionEnum';
import EmbeddedVideo from '@molecules/EmbeddedVideo/EmbeddedVideo';
import FeatureTextContent from '@molecules/FeatureTextContent/FeatureTextContent';
import InViewVideo from '@molecules/InViewVideo/InViewVideo';
import SoftLoadWrapper from '@molecules/SoftLoadWrapper/SoftLoadWrapper';
import useMediaQuery from '@utils/hooks/useMediaQuery';
import useResizeObserver from '@utils/hooks/useResizeObserver';
import classNames from 'classnames';
import { motion, useScroll, useSpring, useTransform } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';

interface Props extends IFeature {
  readonly isSoftLoadDisabled?: boolean;
}

const Feature = ({
  alignment = HorizontalDirectionEnum.Left,
  anchorId,
  autoplay = false,
  categorySpringboard = false,
  contentOverlayToolTip,
  damVideo,
  fullWidth = false,
  image,
  isSoftLoadDisabled,
  meta,
  overlapItem = false,
  overlapItemSmall = false,
  overlapIndex,
  preCTA,
  primaryCTA,
  text,
  title,
  video,
  secondaryImage,
  glow,
  statCards,
}: Props) => {
  const isMobile = useMediaQuery(1024);
  const [isMediaDisplayed, setIsMediaDisplayed] = useState(false);
  const [
    secondaryImageContainerRef,
    {
      height: secondaryImageContainerHeight,
      width: secondaryImageContainerWidth,
    },
  ] = useResizeObserver();
  const mediaContainerRef = useRef<HTMLDivElement>(null);
  const { scrollYProgress } = useScroll({
    target: mediaContainerRef,
    offset: ['start end', 'end start'],
  });
  const smoothScrollYProgress = useSpring(scrollYProgress, {
    stiffness: 300,
    damping: 50,
  });
  const mainImageTranslateYLimit = isMobile ? 60 : 120;
  const mainImageTranslateY = useTransform(
    smoothScrollYProgress,
    [0, 0.5, 1],
    [mainImageTranslateYLimit, 0, -mainImageTranslateYLimit]
  );
  const secondaryImageTranslateYLimit = isMobile ? 120 : 240;
  const secondaryImageTranslateY = useTransform(
    smoothScrollYProgress,
    [0, 0.5, 1],
    [secondaryImageTranslateYLimit, 0, -secondaryImageTranslateYLimit]
  );

  useEffect(() => {
    setIsMediaDisplayed(true);
  }, []);

  const isDefaultLeft =
    alignment === HorizontalDirectionEnum.Left && !fullWidth;
  const isDefaultRight =
    alignment === HorizontalDirectionEnum.Right && !fullWidth;
  const isFullWidthLeft =
    alignment === HorizontalDirectionEnum.Left && fullWidth;
  const isFullWidthRight =
    alignment === HorizontalDirectionEnum.Right && fullWidth;
  const isImageMedia = image && !video?.videoId;
  const hasTwoImages = isImageMedia && secondaryImage;

  const featureTestId = classNames(
    {
      'CT-11-A-Feature-Right': isDefaultRight,
    },
    {
      'CT-11-B-Feature-Left': isDefaultLeft,
    },
    {
      'CT-11-C-Feature-Right-Full-Width': isFullWidthRight,
    },
    {
      'CT-11-D-Feature-Left-Full-Width': isFullWidthLeft,
    }
  );

  const mediaClasses = classNames(
    {
      'xl:col-span-6': alignment === HorizontalDirectionEnum.Left && fullWidth,
    },
    {
      'xl:col-span-7': categorySpringboard && isDefaultLeft && !overlapItem,
    },
    {
      'xl:col-span-6': !categorySpringboard && isDefaultLeft && !overlapItem,
    },
    {
      'lg:col-span-5':
        alignment === HorizontalDirectionEnum.Left &&
        overlapItem &&
        !overlapItemSmall,
    },
    {
      'lg:col-span-4':
        alignment === HorizontalDirectionEnum.Left && overlapItemSmall,
    },
    {
      'xl:col-start-1':
        alignment === HorizontalDirectionEnum.Left && !overlapItemSmall,
    },
    {
      'lg:col-start-2':
        alignment === HorizontalDirectionEnum.Left && overlapItemSmall,
    },
    {
      'xl:col-start-7 xl:col-span-6':
        alignment === HorizontalDirectionEnum.Right && fullWidth,
    },
    {
      'lg:col-start-7 lg:col-span-6 lg:order-last':
        alignment === HorizontalDirectionEnum.Right && overlapItem,
    },
    {
      'xl:col-start-6 xl:col-span-7':
        categorySpringboard && isDefaultRight && !overlapItem,
    },
    {
      'xl:col-start-7 xl:col-span-6':
        !categorySpringboard && isDefaultRight && !overlapItem,
    },
    {
      'xl:order-last':
        alignment === HorizontalDirectionEnum.Right && !overlapItem,
    },
    { 'max-xl:pr-8': isDefaultLeft && hasTwoImages },
    { 'max-xl:pl-8': isDefaultRight && hasTwoImages },
    {
      relative: hasTwoImages,
    },
    'w-full self-center'
  );

  const contentClasses = classNames(
    {
      'xl:col-start-8 xl:col-span-4': isFullWidthLeft,
    },
    {
      'xl:col-start-9 xl:col-span-3':
        categorySpringboard && isDefaultLeft && !overlapItem,
    },
    {
      'xl:col-start-8 xl:col-span-4':
        !categorySpringboard && isDefaultLeft && !overlapItem && !hasTwoImages,
    },
    {
      'xl:col-start-9 xl:col-span-4':
        !categorySpringboard && isDefaultLeft && !overlapItem && hasTwoImages,
    },
    {
      'lg:col-start-8 lg:col-span-4':
        alignment === HorizontalDirectionEnum.Left && overlapItem,
    },
    {
      'xl:col-span-4': isFullWidthRight,
    },
    {
      'lg:col-span-5 xl:col-span-4':
        alignment === HorizontalDirectionEnum.Right && overlapItem,
    },
    {
      'xl:col-span-3': categorySpringboard && isDefaultRight && !overlapItem,
    },
    {
      'xl:col-span-4': !categorySpringboard && isDefaultRight && !overlapItem,
    },
    {
      'lg:col-start-1 xl:col-start-2':
        alignment === HorizontalDirectionEnum.Right && !hasTwoImages,
    },
    {
      'xl:col-start-1': isDefaultRight && hasTwoImages,
    },
    { 'xl:-translate-y-20': overlapIndex === 0 },
    { 'xl:translate-y-20': overlapIndex === 2 },
    overlapItem ? 'lg:mt-0' : 'xl:mt-0',
    hasTwoImages ? 'mt-13' : 'mt-7.5',
    'w-full self-center'
  );

  return (
    <section
      id={`a-${anchorId}`}
      className={classNames(
        'layout two-col-spacing bg-white text-black-100 dark:bg-black-100 dark:text-white',
        { 'component-padding': !overlapItem },
        hasTwoImages ? 'max-xl:overflow-hidden max-xl:pt-10' : 'overflow-hidden'
      )}
      data-epi-block-id={meta?.contentLink}
      data-testid={featureTestId}
    >
      <SoftLoadWrapper isAnimationDisabled={isSoftLoadDisabled}>
        <div
          className={classNames(
            overlapItem ? 'container lg:grid' : 'inner xl:grid',
            'flex grid-cols-12 flex-col xl:grid'
          )}
        >
          <motion.div
            ref={mediaContainerRef}
            className={mediaClasses}
            {...(hasTwoImages
              ? {
                  initial: { opacity: 0 },
                  animate: { opacity: isMediaDisplayed ? 1 : 0 },
                }
              : {})}
            data-epi-property-name={
              isImageMedia ? meta?.editHints?.image : meta?.editHints?.video
            }
          >
            {isImageMedia && (
              <div
                className={classNames({
                  'xl:float-right': alignment === HorizontalDirectionEnum.Left,
                  'xl:w-[50vw]': fullWidth,
                  relative: hasTwoImages,
                })}
              >
                {hasTwoImages && glow && (
                  <motion.img
                    src={`${Hue}`}
                    alt="hue"
                    style={{
                      height: secondaryImageContainerHeight,
                      width: secondaryImageContainerWidth,
                      translateY: secondaryImageTranslateY,
                      scale: 2,
                    }}
                    className={classNames(
                      '-bottom-[15px] xl:-bottom-10 absolute',
                      {
                        '-right-15 xl:-right-24':
                          alignment === HorizontalDirectionEnum.Left,
                        '-left-15 xl:-left-24':
                          alignment === HorizontalDirectionEnum.Right,
                      }
                    )}
                    data-testid={`${featureTestId}-Glow`}
                  />
                )}

                <motion.div
                  style={{
                    ...(hasTwoImages
                      ? { translateY: mainImageTranslateY }
                      : {}),
                  }}
                >
                  <Image
                    image={image}
                    classes={classNames({
                      'xl:flex xl:justify-end xl:mr-12':
                        alignment === HorizontalDirectionEnum.Left && fullWidth,
                    })}
                    imageClasses="w-full"
                    sizes="(min-width: 1024px) 1024px, 100vw"
                    dataTestId={`${featureTestId}-Image`}
                  />
                </motion.div>

                {hasTwoImages && (
                  <motion.div
                    ref={secondaryImageContainerRef}
                    style={{ translateY: secondaryImageTranslateY }}
                    className={classNames(
                      '-bottom-[15px] xl:-bottom-10 absolute flex max-h-[146px] max-w-[176px] md:max-h-[180px] md:max-w-[240px] lg:max-h-[390px] lg:max-w-[470px]',
                      {
                        '-right-8 xl:-right-20':
                          alignment === HorizontalDirectionEnum.Left,
                        '-left-8 xl:-left-20':
                          alignment === HorizontalDirectionEnum.Right,
                      }
                    )}
                  >
                    <div className="relative">
                      <Image
                        image={secondaryImage}
                        imageClasses="h-full object-contain"
                        dataTestId={`${featureTestId}-Secondary-Image`}
                      />
                    </div>
                  </motion.div>
                )}
              </div>
            )}

            {(video?.videoId || damVideo?.url) && (
              <div
                className={classNames({
                  'w-full xl:float-right':
                    alignment === HorizontalDirectionEnum.Left,
                  'overflow-hidden xl:w-[50vw]': fullWidth,
                })}
              >
                {video?.videoId && (
                  <EmbeddedVideo
                    embedded
                    videoProvider={video?.videoProvider}
                    videoId={video?.videoId}
                    coverImage={video?.coverImage}
                    dataTestId={`${featureTestId}-Video`}
                    autoPlay={autoplay}
                  />
                )}

                {damVideo?.url && (
                  <InViewVideo
                    dataTestId={`${featureTestId}-Video`}
                    video={damVideo}
                    autoPlay={autoplay}
                  />
                )}
              </div>
            )}
          </motion.div>

          <div className={contentClasses}>
            <FeatureTextContent
              categorySpringboard={categorySpringboard}
              title={title}
              text={text}
              contentOverlayToolTip={contentOverlayToolTip}
              preCTA={preCTA}
              primaryCTA={primaryCTA}
              meta={meta}
              overlapItem={overlapItem}
              dataTestId={featureTestId}
              statCards={statCards}
              fullWidth={fullWidth}
            />
          </div>
        </div>
      </SoftLoadWrapper>
    </section>
  );
};

export default Feature;
